import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import { api } from '../utils/api';
import { EMAIL_CAL_GROUP } from '../tabPalette';

// ── New-mail pop-up (Calem, report 2e5c8ac7) ─────────────────────────────────────────────────────────
// "pop-up appears on every SLF System page (inbox only) email in the user's default (starred) account
// showing sender + subject, and clicking it opens that email + page the user can turn it on or off."
// Mounted once in AuthGate (App.tsx), so it rides along on every staff page. Each new email in the Inbox
// of the starred account gets a card at the bottom right — sender, subject and a close × — stacked just
// above the corner's round "+" and Joy, newest at the bottom. A card fades after CARD_MS, not while the
// mouse is on it. Clicking it opens the email in PandaAI Email, in the tab that already shows Email (Calem,
// report b6182c13): this one, or the one the browser brings to the front; a new tab only when there is none
// (MailApp reads ?open=). What counts as new, and the user's Email notifications switch (the bell over the
// ACCOUNTS list), are the server's call: GET /api/email/notify.
//
// Only the tab in front asks — once a minute, and again the moment it comes to the front. The browser's
// tabs share one marker and one list of the emails already shown (localStorage), so with ten SLF tabs
// open a new email pops up once, in the tab being looked at, and a tab opened meanwhile carries on from
// the others' marker rather than skipping what came in just before it. When no tab has asked for
// STALE_MS, the first ask starts afresh instead: the morning's first page doesn't pop up the night's mail.

// The tab showing PandaAI Email — /m/email, or the Dashboard on its Email tab — answers to EMAIL_TAB while it does
// (MailAppView), and opens the email in an EMAIL_OPEN_MSG sent to it.
export const EMAIL_TAB = 'slf-email';
export const EMAIL_OPEN_MSG = 'slf-email-open';
type EmailOpenMsg = { type: typeof EMAIL_OPEN_MSG; open: string; account: string | null };

const POLL_MS = 60_000;
const CARD_MS = 30_000;          // how long a card stays
const AFTER_HOVER_MS = 4_000;    // once the mouse leaves it
const FADE_MS = 400;
const STALE_MS = 15 * 60_000;
const MAX_CARDS = 5;
const SEEN_KEEP = 100;

type Card = { id: string; accountId: string | null; from: string; fromEmail: string; subject: string };
type Shared = { since: number; at: number; seen: string[] };   // at = when a tab last asked (this browser's clock)
const sharedKey = (userId: string) => `mf_newMail:${userId}`;
function readShared(userId: string): Shared | null {
  try {
    const v = JSON.parse(localStorage.getItem(sharedKey(userId)) || 'null');
    return v && Number.isFinite(v.since) && Number.isFinite(v.at) && Array.isArray(v.seen) ? v : null;
  } catch { return null; }
}
function writeShared(userId: string, v: Shared): void {
  try { localStorage.setItem(sharedKey(userId), JSON.stringify(v)); } catch { /* storage full — this tab still works */ }
}

export default function NewMailToast() {
  const me = useStore(s => s.me);
  const userId = me?.userId;
  // The endpoint sits behind the email module; asking without it would be refused, and a refusal pages
  // the admins.
  const canEmail = !!me?.modules?.includes('email');
  const [cards, setCards] = useState<Card[]>([]);
  const busy = useRef(false);

  const poll = useCallback(async () => {
    if (!userId || !canEmail || busy.current || document.visibilityState !== 'visible' || !localStorage.getItem('mf_token')) return;
    busy.current = true;
    try {
      const prev = readShared(userId);
      const carryOn = !!prev && Date.now() - prev.at < STALE_MS;
      const res = await api.getNewMail(carryOn ? prev!.since : undefined);
      // Read again: another tab may have shown some of these while this one was asking.
      const seen = carryOn ? (readShared(userId)?.seen || prev!.seen) : [];
      const fresh = res.on ? res.emails.filter(e => !seen.includes(e.id)) : [];
      writeShared(userId, { since: res.since, at: Date.now(), seen: [...seen, ...fresh.map(e => e.id)].slice(-SEEN_KEEP) });
      if (fresh.length) {
        // Latest first from the server; the stack runs oldest at the top to newest at the bottom.
        const add: Card[] = fresh.slice().reverse().map(e => ({
          id: e.id, accountId: res.accountId, from: e.from_name || e.from_email, fromEmail: e.from_email, subject: e.subject,
        }));
        setCards(cs => [...cs, ...add.filter(a => !cs.some(c => c.id === a.id))].slice(-MAX_CARDS));
      }
    } catch { /* transient — the next minute asks again */ }
    finally { busy.current = false; }
  }, [userId, canEmail]);

  useEffect(() => {
    if (!userId || !canEmail) return;
    void poll();
    const timer = window.setInterval(() => { void poll(); }, POLL_MS);
    const onShow = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
  }, [userId, canEmail, poll]);

  const drop = (id: string) => setCards(cs => cs.filter(c => c.id !== id));
  const open = (c: Card) => {
    const msg: EmailOpenMsg = { type: EMAIL_OPEN_MSG, open: c.id, account: c.accountId };
    if (window.name === EMAIL_TAB) window.postMessage(msg, window.location.origin);   // this tab is the Email one
    else {
      // Brings the Email tab to the front, or opens a blank tab when there is none. Only that tab is sent the
      // email; another Email view left open elsewhere keeps what it shows.
      const w = window.open('', EMAIL_TAB);
      let blank = false;
      try { blank = w?.location.href === 'about:blank'; } catch { /* moved to another site — not ours to send to */ }
      if (w && blank) {
        w.name = '';   // the Email page names it once it is showing, so a page that never gets there doesn't keep it
        const acct = c.accountId ? `&account=${encodeURIComponent(c.accountId)}` : '';
        w.location.href = `/m/email?open=${encodeURIComponent(c.id)}${acct}`;
      } else w?.postMessage(msg, window.location.origin);
    }
    drop(c.id);
  };

  // Under the dialogs (5400 and up) and Joy (9000); over the page, its "+" (100), the toast line (2000)
  // and the upload card (4000). The empty stack lets clicks through.
  return (
    <div aria-live="polite" style={{ position: 'fixed', right: 24, bottom: 92, zIndex: 4500, width: 320, maxWidth: 'calc(100vw - 48px)', display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
      {cards.map(c => <MailCard key={c.id} card={c} onOpen={() => open(c)} onClose={() => drop(c.id)} />)}
    </div>
  );
}

function MailCard({ card, onOpen, onClose }: { card: Card; onOpen: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const fadeAfter = (ms: number) => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setLeaving(true), ms); };
  useEffect(() => { fadeAfter(CARD_MS); return () => window.clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Gone once the fade has run; the mouse coming back over it in time brings it back.
  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(onClose, FADE_MS);
    return () => window.clearTimeout(id);
  }, [leaving]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div role="status"
      onMouseEnter={() => { window.clearTimeout(timer.current); setLeaving(false); }}
      onMouseLeave={() => fadeAfter(AFTER_HOVER_MS)}
      style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'flex-start', gap: 6, padding: '10px 8px 10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${EMAIL_CAL_GROUP.color}`, borderRadius: 12, boxShadow: 'var(--shadow-md)', animation: 'slideUp 0.25s ease', opacity: leaving ? 0 : 1, transition: `opacity ${FADE_MS}ms ease` }}>
      <button onClick={onOpen} title={t('eml_notify_open')}
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10, padding: 0, border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
        <span style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: EMAIL_CAL_GROUP.tint, color: EMAIL_CAL_GROUP.color }}>
          <i className="ti ti-mail" style={{ fontSize: 16 }} aria-hidden />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: EMAIL_CAL_GROUP.color }}>{t('eml_notify_new')}</span>
          <span title={card.fromEmail} style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.from}</span>
          <span style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--text-secondary)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{card.subject}</span>
        </span>
      </button>
      <button onClick={onClose} title={t('eml_notify_close')} aria-label={t('eml_notify_close')}
        style={{ flexShrink: 0, width: 22, height: 22, padding: 0, borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
      </button>
    </div>
  );
}
